"use client";

import React, { useMemo } from "react";
import ReactSelect from "react-select";
import "./MiniSelect.css";

export interface MiniSelectOption {
  label: string;
  value: string;
}

interface MiniSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: MiniSelectOption[];
  placeholder?: string;
  isDisabled?: boolean;
  className?: string;
}

const MiniSelect: React.FC<MiniSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
  isDisabled = false,
  className = "",
}) => {
  const selected = options.find((opt) => opt.value === value) || null;

  const isBrowser = typeof window !== "undefined";

  const isMobile = useMemo(() => {
    if (!isBrowser) return false;
    return /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent || "");
  }, [isBrowser]);

  const portalTarget = useMemo(() => {
    if (!isBrowser) return undefined;

    // ✅ Mobile: use a stable overlay root (prevents "floating" issues)
    if (isMobile) {
      return (
        document.getElementById("select-portal-root") ??
        document.body
      );
    }

    // ✅ Desktop: body is fine
    return document.body;
  }, [isBrowser, isMobile]);

  return (
    <div className={`mini-select-wrapper ${className}`}>
      <ReactSelect
        options={options}
        value={selected}
        onChange={(option) => onChange(option?.value || "")}
        placeholder={placeholder}
        isDisabled={isDisabled}
        classNamePrefix="mini-select"

        /* ✅ Portal always on (solves z-index + clipping) */
        menuPortalTarget={portalTarget}

        /* ✅ Positioning by platform */
        menuPosition={isMobile ? "absolute" : "fixed"}
        menuPlacement="bottom"

        /* ✅ Keep open during scroll; user intent is selection */
        closeMenuOnScroll={false}
        closeMenuOnSelect={true}

        styles={{
          menuPortal: (base) => ({
            ...base,
            zIndex: 999999, // 🔥 always above badges
          }),
          menu: (base) => ({
            ...base,
            marginTop: 2,
          }),
        }}
      />
    </div>
  );
};

export default MiniSelect;